import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLoaderContext } from '../../contexts/LoaderContext';
import { useTranslation } from 'react-i18next';
import { IModalConfig } from '../../types/modal';
import { IDialogConfig } from '../../types/dialog';
import Modal from '../common/modal/Modal';
import Button from 'devextreme-react/button';
import icons from '../common/icons/icons';
import ConfirmDialog from '../common/confirm/ConfirmDialog';
import { MarsApiService as api } from '../../api/mars-api-service';
import { MarsApiConfig as apiConfig } from '../../api/mars-api-config';
import toast from '../../utils/toast';
import { IFleetVesselObject } from '../../types/list';
import SelectBox from 'devextreme-react/select-box';
import { RequiredRule, Validator } from 'devextreme-react/validator';
import ValidationGroup from 'devextreme-react/validation-group';
import { Fleet } from '../../types/fleet';
import { AddToFleetModalProps } from '../../types/fleet';

export default function AddToFleetModal({
  isVisible,
  setIsVisible,
  vesselIMO,
  vesselName,
  fleetId,
  onVesselAdded,
}: AddToFleetModalProps) {
  const { setIsLoading } = useLoaderContext();
  const { t } = useTranslation();
  const [confirmSaveDialog, setConfirmSaveDialog] = useState(false);
  const [selectedFleetId, setSelectedFleetId] = useState<number | undefined>(
    fleetId
  );
  const [selectedFleetName, setSelectedFleetName] = useState<string>('');
  const [fleetList, setFleetList] = useState<Fleet[]>([]);

  const getFleetList = async () => {
    try {
      const data = await api.get({ url: apiConfig.myWatchList }, setIsLoading);
      const activeFleets = (data || []).filter((fleet: Fleet) => fleet.active);
      setFleetList(activeFleets);
    } catch (err) {
      console.error('Error fetching fleet list:', err);
      setFleetList([]);
    }
  };

  useEffect(() => {
    if (isVisible && !fleetId) {
      getFleetList();
    }
  }, [isVisible, fleetId, setIsLoading]);

  const handleFleetSelect = (e: any) => {
    const selectedId = e.value;
    const fleet = fleetList.find((f) => f.fleetId === selectedId);
    setSelectedFleetId(selectedId);
    setSelectedFleetName(fleet?.fleetName || '');
  };

  const handleAddClick = () => {
    if (!selectedFleetId || !vesselIMO) {
      return;
    }

    setActiveDialogConfig({
      id: 'confirmAddToFleet',
      title: t('toast.confirmation'),
      content: t('toast.addVesselToFleet', {
        vesselName: vesselName || 'Vessel',
        fleetName: selectedFleetName,
      }),
      handleSubmit: async () => {
        setConfirmSaveDialog(false);
        const fleetVesselObject: IFleetVesselObject = {
          fleetId: selectedFleetId,
          imonumber: vesselIMO,
          active: true,
        };
        try {
          await api.post(
            { url: apiConfig.fleetVesselList, data: fleetVesselObject },
            setIsLoading
          );
          toast.success({
            title: t('toast.fleetVesselAddedSuccess'),
          });
          setIsVisible(false);
          if (onVesselAdded) {
            onVesselAdded();
          }
        } catch (error) {
          console.error('Error adding vessel to fleet:', error);
        }
      },
      handleClose: () => {
        setConfirmSaveDialog(false);
      },
    });
    setConfirmSaveDialog(true);
  };

  const confirmStatusDialogConfig: IDialogConfig = {
    id: 'confirmSave',
    handleSubmit: () => {
      setConfirmSaveDialog(false);
    },
    handleClose: () => {
      setConfirmSaveDialog(false);
    },
  };

  const [activeDialogConfig, setActiveDialogConfig] = useState(
    confirmStatusDialogConfig
  );

  const handleCancelClick = () => {
    setIsVisible(false);
    setSelectedFleetId(fleetId);
    setSelectedFleetName('');
  };

  const modalConfig: IModalConfig = {
    width: 340,
    height: 200,
    visible: isVisible,
    showTitle: false,
    closeOnOutsideClick: false,
  };

  const popupAttributes = useMemo(() => {
    return {
      id: 'elementId',
      class: 'm-l-modal-main__wrap m-l-add-new-modal',
    };
  }, []);

  const validationGroup = useRef<any>();

  return (
    <>
      <Modal modalConfig={modalConfig} wrapperAttr={popupAttributes}>
        <div className="m-l-modal__header">
          <h2 className="modal-title">{t('headers.addToFleet')}</h2>
          <div className="m-l-modal-close-button-block">
            <Button
              className="app-c-btn app-c-icon-only-btn"
              onClick={handleCancelClick}
            >
              <icons.IconCross />
            </Button>
          </div>
        </div>
        <div className="m-l-modal__body">
          <div className="m-l-adnw-form-panel">
            {!fleetId && (
              <div className="fleet-selection">
                <ValidationGroup ref={validationGroup}>
                  {fleetList.length > 0 && (
                    <SelectBox
                      searchEnabled={true}
                      valueExpr="fleetId"
                      displayExpr="fleetName"
                      dataSource={fleetList}
                      className="m-c-input-control m-c-select-box"
                      value={selectedFleetId}
                      onValueChanged={handleFleetSelect}
                      validationMessageMode="always"
                    >
                      <Validator>
                        <RequiredRule message={t('errors.required')} />
                      </Validator>
                    </SelectBox>
                  )}
                </ValidationGroup>
              </div>
            )}
          </div>
        </div>
        <div className="m-l-modal__footer">
          <Button
            className="app-c-btn app-c-btn--primary min-btn-width"
            onClick={handleAddClick}
          >
            {t('buttons.add')}
          </Button>
        </div>
      </Modal>
      <ConfirmDialog
        dialogConfig={activeDialogConfig}
        isOpen={confirmSaveDialog}
      />
    </>
  );
}
